import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

const CloudIcon = () => <span className="material-icons text-[20px]">cloud</span>;
const FileTextIcon = () => <span className="material-icons text-[20px]">description</span>;
const LineChartIcon = () => <span className="material-icons text-[20px]">show_chart</span>;
const PenSquareIcon = () => <span className="material-icons text-[20px]">edit</span>;
const SearchIcon = () => <span className="material-icons text-[20px]">search</span>;
const LayoutDashboardIcon = () => <span className="material-icons text-[20px]">dashboard</span>;
const CloseIcon = () => <span className="material-icons text-[20px]">close</span>;
const MenuIcon = () => <span className="material-icons text-[20px]">menu</span>;
const UsersIcon = () => <span className="material-icons text-[20px]">groups</span>;
const HeadsetIcon = () => <span className="material-icons text-[20px]">headset</span>;
const BookOpenIcon = () => <span className="material-icons text-[20px]">import_contacts</span>;
const SettingsIcon = () => <span className="material-icons text-[20px]">settings</span>;
const ReportIcon = () => (<span className="material-icons text-[20px]">assessment</span>);
const CrowdIcon = () => (<span className="material-icons text-[20px]">volunteer_activism</span>);
const FundingIcon = () => (
  <span className="material-icons text-[20px]">attach_money</span>);
const AudienceIcon = () => (
  <span className="material-icons text-[20px]">group</span>);


interface SidebarProps {
    onProfileClick: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

export const Item = ({
  to,
  icon,
  label,
  isCollapsed,
  hideWhenInactive,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  isCollapsed?: boolean;
  hideWhenInactive?: boolean;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const location = useLocation();

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({ top: rect.top });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // ✅ Exact match for dashboard
  const isActive =
    to === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname === to ||
        location.pathname.startsWith(to + "/");

  // ✅ hideWhenInactive works properly now
  if (hideWhenInactive && !isActive) {
    return null;
  }

  return (
    <>
      <NavLink
        to={to}
        end={to === "/dashboard"} // 🔥 VERY IMPORTANT
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={({ isActive }) =>
          [
            "flex items-center gap-3 px-3 py-2 rounded-[12px] transition relative group",
            isActive
              ? "bg-black/[0.2] text-white"
              : "hover:bg-black/[0.04] text-white/80",
            isCollapsed ? "justify-center" : "",
          ].join(" ")
        }
        title={isCollapsed ? label : ""}
      >
        <span className="h-5 w-5 rounded-full grid place-items-center text-[#93C893] bg-[#202220] flex-shrink-0">
          {icon}
        </span>

        {!isCollapsed && (
          <span className="font-medium text-sm">{label}</span>
        )}
      </NavLink>

      {isCollapsed && showTooltip && (
        <div
          className="fixed left-[78px] bg-gray-800 text-white px-3 py-2 rounded-md text-sm whitespace-nowrap z-[9999] shadow-lg pointer-events-none"
          style={{ top: `${tooltipPosition.top}px` }}
        >
          {label}
        </div>
      )}
    </>
  );
};

// Mobile Sidebar Component
const MobileSidebar = ({
    isOpen,
    onClose,
    onProfileClick
}: {
    isOpen: boolean;
    onClose: () => void;
    onProfileClick: boolean;
}) => {
    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                onClick={onClose}
            />

            <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50 lg:hidden transform transition-transform">
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="font-semibold">
                        {onProfileClick ? "Settings" : "Main"}
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
                        <CloseIcon />
                    </button>
                </div>

                <div className="p-3">
                    <div className="px-3 py-2">
                        <div className="text-xs">Navigation menu</div>
                    </div>
                    <div className="space-y-1">
                        <Item to="/dashboard" icon={<LayoutDashboardIcon />} label="Dashboard" />
                        <p className="text-white">Main</p>
                        <Item to="/dashboard/discover" icon={<SearchIcon />} label="Discover" />
                        <Item to="/dashboard/write" icon={<PenSquareIcon />} label="Write" hideWhenInactive />
                        <Item to="/dashboard/report" icon={<ReportIcon />} label="Reports" />
                        <Item to="/dashboard/crowdfunding" icon={<CrowdIcon />} label="Crowd Funding" />
                        <Item to="/dashboard/audience" icon={<AudienceIcon />} label="Audience" />
                        <Item to="/dashboard/track" icon={<LineChartIcon />} label="Track" />
                        <Item to="/dashboard/organize" icon={<CloudIcon />} label="Organize" />
                    </div>
                </div>
            </div>
        </>
    );
};

export default function Sidebar({ onProfileClick, isCollapsed = false, onToggleCollapse }: SidebarProps) {
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isStartupOpen, setIsStartupOpen] = useState(false);
    const [showStartupTooltip, setShowStartupTooltip] = useState(false);
    const [startupTooltipTop, setStartupTooltipTop] = useState(0);

    const location = useLocation();
    
    const savedGrantId = location.pathname.match(/\/dashboard\/write\/(\d+)/)?.[1];
    const writeLink = savedGrantId ? `/dashboard/write/${savedGrantId}` : '/dashboard/write';

    /**
     * When the sidebar is collapsed and user clicks Startup Toolkit:
     *  1. Expand the sidebar first (via onToggleCollapse)
     *  2. Open the submenu
     * When the sidebar is expanded, just toggle the submenu.
     */
    const handleStartupClick = () => {
        if (isCollapsed) {
            // Expand sidebar so submenu becomes visible
            onToggleCollapse?.();
            setIsStartupOpen(true);
        } else {
            setIsStartupOpen((prev) => !prev);
        }
    };

    const handleStartupMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isCollapsed) {
            const rect = e.currentTarget.getBoundingClientRect();
            setStartupTooltipTop(rect.top);
            setShowStartupTooltip(true);
        }
    };

    const handleStartupMouseLeave = () => {
        setShowStartupTooltip(false);
    };

    return (
        <>
            <button
                onClick={() => setIsMobileOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-white shadow-md hover:bg-gray-50"
            >
                <MenuIcon />
            </button>

            <MobileSidebar
                isOpen={isMobileOpen}
                onClose={() => setIsMobileOpen(false)}
                onProfileClick={onProfileClick}
            />

            <aside
                className={`hidden lg:block flex-shrink-0 overflow-hidden py-0 transition-all duration-300 ${
                    isCollapsed ? "w-20" : "w-64"
                }`}
            >
                <div
                    className="h-full shadow border p-0 overflow-y-auto bg-[#202220]"
                    style={{scrollbarWidth: "none", msOverflowStyle: "none"}} 
                >
                    <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                    <div className="flex items-center justify-between mb-4 sticky top-0 z-50 bg-[#111827] p-2">
                        {!isCollapsed && (
                            <div className="px-3 py-2">
                                <div className="font-semibold text-white">Main</div> 
                                <div className="text-xs text-gray-300">Navigation menu</div>
                            </div>
                        )}
                        <button
                            onClick={onToggleCollapse}
                            className={`rounded-lg text-white hover:bg-gray-700 p-2 transition 
                                ${isCollapsed ? "w-full" : ""}`}
                            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        >
                            <MenuIcon />
                        </button>
                    </div>

                    <div className="flex flex-col gap-2 my-2 px-2">
                        <Item to="/dashboard" icon={<LayoutDashboardIcon />} label="Dashboard" isCollapsed={isCollapsed} />
                        {!isCollapsed && <p className="pt-5 px-2 text-white">Main</p>}
                        <Item to="/dashboard/discover" icon={<SearchIcon />} label="Discover" isCollapsed={isCollapsed} />
                        <Item to={writeLink} icon={<PenSquareIcon />} label="Write" isCollapsed={isCollapsed} hideWhenInactive />
                        <Item to="/dashboard/track" icon={<LineChartIcon />} label="Track" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/organize" icon={<CloudIcon />} label="Organize" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/report" icon={<ReportIcon />} label="Report" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/crowdfunding" icon={<CrowdIcon />} label="Crowd Funding" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/audience" icon={<AudienceIcon />} label="Audience" isCollapsed={isCollapsed} />
                        {!isCollapsed && <p className="pt-5 px-2 text-white">General</p>}

                        {/* Startup Toolkit accordion — clicking when collapsed expands sidebar + opens submenu */}
                        <div
                            onClick={handleStartupClick}
                            onMouseEnter={handleStartupMouseEnter}
                            onMouseLeave={handleStartupMouseLeave}
                            className={`flex items-center gap-3 px-3 py-2 rounded-[12px] cursor-pointer
                                hover:bg-black/[0.04] text-white transition
                                ${isCollapsed ? "justify-center" : ""}`}
                        >
                            <span className="h-5 w-5 rounded-full grid place-items-center text-[#93C893] bg-[#202220] flex-shrink-0">
                                <BookOpenIcon />
                            </span>

                            {!isCollapsed && (
                                <>
                                    <span className="font-medium text-sm flex-1">Startup Toolkit</span>
                                    <span className="text-lg">
                                        {isStartupOpen ? "−" : "+"}
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Tooltip when collapsed */}
                        {isCollapsed && showStartupTooltip && (
                            <div
                                className="fixed left-[78px] bg-gray-800 text-white px-3 py-2 rounded-md text-sm
                                            whitespace-nowrap z-[9999] shadow-lg pointer-events-none"
                                style={{ top: `${startupTooltipTop}px` }}
                            >
                                Startup Toolkit (click to expand)
                            </div>
                        )}

                        {isStartupOpen && !isCollapsed && (
                            <div className="ml-8 flex flex-col gap-1">
                                <Item
                                    to="/dashboard/fundingtoolkit"
                                    icon={<FundingIcon />}
                                    label="Funding Toolkit"
                                />
                            </div>
                        )}

                        <Item to="/dashboard/community" icon={<UsersIcon />} label="Community" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/support" icon={<HeadsetIcon />} label="Support" isCollapsed={isCollapsed} />
                        <Item to="/dashboard/accountmanagement" icon={<SettingsIcon />} label="Settings" isCollapsed={isCollapsed} />
                    </div>
                </div>
            </aside>
        </>
    );
}